import {
  makeStyles,
  Dialog,
  DialogTitle,
  DialogContent,
  Grid,
  Link,
  Typography,
  debounce,
} from "@material-ui/core";
import SanitizedGrid from "components/SanitizedGrid";
import { useCallback, useEffect, useState } from "react";
import { Button, SimpleForm, useDataProvider } from "react-admin";
import AccountCircleIcon from "@material-ui/icons/AccountCircle";
import CustomTooltip from "components/CustomTooltip";
import AutocompleteManageStudentsInput from "./AutocompleteManageStudentsInput";
import SaveManageStudentsButton from "./SaveManageStudentsButton";

interface IManageStudentsButton {
  routeLocationData: any;
  manageStudentCallback: any;
  selectedStudentIds: number[];
  schoolData: ISchool;
  busScheduleType: string;
  routesData: any;
  selectedMarkers: any;
}

const useStyles = makeStyles({
  dialog: {
    "& .MuiDialog-paper": {
      overflowY: "unset",
    },
  },
  closeTextWrapper: {
    float: "right",
    margin: "7px 4px 0 0",
    cursor: "pointer",
  },
  closeText: {
    color: "#474747",
    fontWeight: 600,
    textDecoration: "underline",
  },
  manageStudentsTooltip: {
    backgroundColor: "#50AD57 !important",
    marginTop: "-15px",
  },
  manageStudentsButton: {
    padding: "5px",
    marginLeft: "5px",
    minWidth: "10px",
    backgroundColor: "#50AD57",
    boxShadow: "0 4px 8px rgb(80 173 87 / 30%)",
    "& .MuiButton-textSizeSmall": {
      padding: 0,
    },
    "& .MuiButton-label > .MuiSvgIcon-root": {
      fontSize: 20,
    },
    "&:hover": {
      backgroundColor: "#50AD57 !important",
    },
  },
});

const ManageStudentsButton = ({
  routeLocationData,
  manageStudentCallback,
  selectedStudentIds,
  schoolData,
  busScheduleType,
  routesData,
  selectedMarkers,
}: IManageStudentsButton) => {
  const [open, setOpen] = useState<boolean>(false);
  const [students, setStudents] = useState<IStudent[]>([]);
  const [searchValue, setSearchValue] = useState<string>("");
  const [values, setValues] = useState<number[]>([]);

  const provider = useDataProvider();
  const classes = useStyles();

  const handleOpenDialog = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setValues([]);
  };

  const getStudents = useCallback(
    debounce(async (searchString: string, selectedStudentIds: number[]) => {
      try {
        const filter: any = {
          SchoolId: Number(schoolData.id),
          Status: "active",
        };

        if (busScheduleType === "cca") {
          filter.HasCcaSchedule = true;
        }

        if (
          busScheduleType === "to_school" ||
          busScheduleType === "from_school"
        ) {
          filter.HasRegularSchedule = true;
        }

        const searchStudent = await provider.customGetListWithStaticParams(
          `students?filter=${JSON.stringify(
            filter,
          )}&pageSize=50&search=${searchString}`,
        );

        const defaultStudent = await provider.customGetListWithStaticParams(
          `students?ids=${
            selectedStudentIds.length > 0
              ? selectedStudentIds
              : routeLocationData.students.map(
                  (student: IStudent) => student.id,
                )
          }`,
        );

        setStudents([...searchStudent.data, ...defaultStudent.data]);
      } catch (error) {
        console.log("ERROR", error);
      }
    }, 1000),
    [],
  );

  useEffect(() => {
    if (
      searchValue !== "" &&
      searchValue !== undefined &&
      typeof searchValue === "string"
    ) {
      getStudents(searchValue, values);
    }
  }, [searchValue, getStudents, values]);

  return (
    <>
      <CustomTooltip
        title="Manage Students"
        aria-label="Manage Students"
        classes={{
          tooltip: classes.manageStudentsTooltip,
        }}
      >
        <Button
          className={classes.manageStudentsButton}
          onClick={handleOpenDialog}
        >
          <AccountCircleIcon />
        </Button>
      </CustomTooltip>
      <Dialog
        open={open}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        className={classes.dialog}
        maxWidth="xs"
      >
        <DialogTitle id="alert-dialog-title">
          {`${routeLocationData.blockNumber} ${routeLocationData.street}`}
          <Link
            aria-label="close"
            className={classes.closeTextWrapper}
            onClick={handleClose}
          >
            <Typography variant="body2" className={classes.closeText}>
              Cancel
            </Typography>
          </Link>
        </DialogTitle>
        <DialogContent>
          <SimpleForm toolbar={null}>
            <SanitizedGrid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="subtitle2">
                  Students included on this location
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <AutocompleteManageStudentsInput
                  routeLocationData={routeLocationData}
                  selectedStudentIds={selectedStudentIds}
                  allStudents={students}
                  setSearchValue={setSearchValue}
                  setValues={setValues}
                />
              </Grid>

              <Grid item xs={12}>
                <SaveManageStudentsButton
                  routeLocationData={routeLocationData}
                  manageStudentCallback={(value: any) =>
                    manageStudentCallback(value)
                  }
                  handleClose={handleClose}
                  routesData={routesData}
                  selectedMarkers={selectedMarkers}
                />
              </Grid>
            </SanitizedGrid>
          </SimpleForm>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ManageStudentsButton;
